import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { apiUrl, getErrorMessage } from '../utils/api';

const initialForm = { name: '', email: '', password: '', confirmPassword: '' };

function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const userInfo = useMemo(() => JSON.parse(localStorage.getItem('userInfo') || 'null'), []);
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(apiUrl(`/api/owner/invites/${token}`));
        setInvite(data);
        setForm((current) => ({
          ...current,
          name: userInfo?.name || current.name,
          email: data.email,
        }));
      } catch (err) {
        setError(getErrorMessage(err, 'Could not load this invite.'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, userInfo?.name]);

  const claim = async (payload = {}) => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const config = userInfo?.token ? { headers: { Authorization: 'Bearer ' + userInfo.token } } : undefined;
      const { data } = await axios.post(apiUrl(`/api/owner/invites/${token}/claim`), payload, config);
      localStorage.setItem('userInfo', JSON.stringify(data.user));
      window.dispatchEvent(new Event('authchange'));
      setMessage(`Invite accepted for ${data.business.name}.`);
      navigate(data.user?.role === 'staff' ? '/my-schedule' : '/owner');
    } catch (err) {
      setError(getErrorMessage(err, 'Could not accept invite.'));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!invite) return;
    if (userInfo?.token) {
      await claim({});
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    await claim({ name: form.name, email: form.email, password: form.password });
  };

  if (loading) {
    return (
      <div className="page">
        <div className="panel">
          <p className="eyebrow">Invite setup</p>
          <h1>Loading invite…</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="page auth-page">
      <form className="panel auth-card" onSubmit={submit}>
        <p className="eyebrow">Owner onboarding</p>
        <h1>Accept invite</h1>
        {invite && (
          <>
            <p className="muted-text">You’ve been invited to join <strong>{invite.business?.name}</strong> as <strong>{invite.role}</strong>.</p>
            <p className="muted-text">Invite email: <strong>{invite.email}</strong></p>
          </>
        )}
        {!userInfo?.token && (
          <>
            <input type="text" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <input type="password" placeholder="Create password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <input type="password" placeholder="Confirm password" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} required />
          </>
        )}
        {userInfo?.token && <p className="muted-text">You’re signed in already. Click below to attach this account to the business.</p>}
        <button type="submit" disabled={busy}>{busy ? 'Accepting…' : 'Accept Invite'}</button>
        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
        <p className="muted-text">Already have an account? <Link to="/login">Log in</Link>.</p>
      </form>
    </div>
  );
}

export default AcceptInvite;
